import { api } from "./api";
import {
  FIXTURE_ADMIN_USERS,
  FIXTURE_CHANGELOG,
  FIXTURE_COUNTRIES,
  FIXTURE_ENTITIES,
  FIXTURE_RENEWALS,
  type AdminUserFixture,
} from "./fixtures";
import type {
  ChangeLog,
  Country,
  Director,
  DirectorCreate,
  DirectorPatch,
  DirectorsResponse,
  Document,
  DocumentListResponse,
  Entity,
  MarkObligationRenewedParams,
  Obligation,
  ObligationCreate,
  ObligationPatch,
  ObligationsResponse,
  OwnershipResponse,
  OwnershipStakeCreate,
  OwnershipStakePatch,
  Region,
  RenewalEvent,
  RenewalStatusResponse,
  RuleChangeCandidate,
  RegulatorySource,
  RegwatchDomain,
  TypedEntityFields,
} from "./types";

// Re-export Country for consumers of patchCountryProviders
export type { Country };

// Re-exported for external use
export type { Obligation };

/**
 * Each fetcher tries the live backend first. If the request fails for any
 * reason (network, CORS, 5xx, backend not running yet) it falls back to
 * fixtures so the UI is always buildable and previewable.
 *
 * 401 is *not* swallowed — the axios interceptor will redirect to /signin.
 */

async function withFallback<T>(label: string, run: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await run();
  } catch (err) {
    if (typeof window !== "undefined") {
      // Surface that we're rendering against fixtures so devs notice.
      // eslint-disable-next-line no-console
      console.warn(`[fixtures] ${label} fell back:`, (err as Error).message);
    }
    return fallback;
  }
}

export interface EntitiesListResponse {
  entities: Entity[];
  total: number;
  page: number;
  limit: number;
}

export interface FetchEntitiesParams {
  q?: string;
  region?: string;
  country?: string;
  countryId?: string;
  status?: string;
  hasEor?: boolean;
  page?: number;
  limit?: number;
}

export async function fetchEntities(
  params: FetchEntitiesParams = {},
): Promise<EntitiesListResponse> {
  return withFallback(
    "GET /entities",
    async () => {
      const res = await api.get<EntitiesListResponse>("/entities", { params });
      return res.data;
    },
    {
      entities: FIXTURE_ENTITIES,
      total: FIXTURE_ENTITIES.length,
      page: 1,
      limit: 100,
    },
  );
}

export async function fetchEntity(id: string): Promise<Entity & { changes: ChangeLog[] }> {
  return withFallback(
    `GET /entities/${id}`,
    async () => {
      const res = await api.get<Entity & { changes: ChangeLog[] }>(`/entities/${id}`);
      return res.data;
    },
    (() => {
      const e = FIXTURE_ENTITIES.find((x) => x.id === id) ?? FIXTURE_ENTITIES[0];
      const changes = FIXTURE_CHANGELOG.filter((c) => c.entityId === e.id);
      return { ...e, changes };
    })(),
  );
}

export async function fetchCountries(): Promise<Country[]> {
  return withFallback(
    "GET /countries",
    async () => {
      const res = await api.get<Country[]>("/countries");
      return res.data;
    },
    FIXTURE_COUNTRIES,
  );
}

// ─── Regions list ────────────────────────────────────────────────────────────

export interface RegionItem {
  value: string;
  label: string;
}

/**
 * GET /api/regions
 * Returns the ordered canonical region list for display (no deprecated values).
 * staleTime: Infinity — regions change only at deploy time.
 * Falls back to the canonical hard-coded list when the endpoint is absent.
 */
export async function fetchRegions(): Promise<RegionItem[]> {
  return withFallback(
    "GET /regions",
    async () => {
      const res = await api.get<RegionItem[]>("/regions");
      return res.data;
    },
    [
      { value: "EUROPE",        label: "Europe" },
      { value: "MIDDLE_EAST",   label: "Middle East" },
      { value: "AFRICA",        label: "Africa" },
      { value: "NORTH_AMERICA", label: "North America" },
      { value: "LATIN_AMERICA", label: "Latin America" },
      { value: "ASIA_PACIFIC",  label: "Asia Pacific" },
    ],
  );
}

// ─── Country create (new-country-support) ────────────────────────────────────

export interface CountryCreatePayload {
  name: string;
  /** Exactly 2 characters, uppercased before sending. */
  iso2: string;
  region: Region;
  /** Optional flag emoji; backend defaults to "🌐" when omitted. */
  flag?: string;
}

/**
 * POST /api/countries
 * Creates a new Country row. Does NOT fall back to fixtures — this is a
 * mutating call; failures (409 duplicate name/iso2, 422) must surface to the caller.
 * Returns the created Country (201 CountryRead).
 */
export async function createCountry(payload: CountryCreatePayload): Promise<Country> {
  const res = await api.post<Country>("/countries", payload);
  return res.data;
}

// ─── Regulatory Watch last-run status (Change D) ──────────────────────────────

export interface RegulatoryLastRun {
  /** ISO-8601 UTC timestamp of the most recent source check; null if never run. */
  lastCheckedAt: string | null;
  /** Count of rule-change candidates with status="new" detected today (UTC). */
  newToday: number;
}

/**
 * GET /api/regulatory/last-run
 * Accessible by all authenticated roles (user, legal_advisor, admin).
 * Falls back to { lastCheckedAt: null, newToday: 0 } on DB/network failure
 * so the bell degrades gracefully to "Not yet run."
 */
export async function fetchRegulatoryLastRun(): Promise<RegulatoryLastRun> {
  return withFallback(
    "GET /regulatory/last-run",
    async () => {
      const res = await api.get<RegulatoryLastRun>("/regulatory/last-run");
      return res.data;
    },
    { lastCheckedAt: null, newToday: 0 },
  );
}

export async function fetchRenewals(params: Record<string, string | undefined> = {}): Promise<RenewalEvent[]> {
  return withFallback(
    "GET /renewals",
    async () => {
      // Backend returns { events: RenewalEvent[], meta: {...} }; we unwrap to keep
      // every caller working with a bare array. Meta counts can be derived from
      // events client-side, so no separate fetcher is needed yet.
      const res = await api.get<{ events: RenewalEvent[] } | RenewalEvent[]>("/renewals", { params });
      return Array.isArray(res.data) ? res.data : (res.data.events ?? []);
    },
    FIXTURE_RENEWALS,
  );
}

export interface ChangelogListResponse {
  items: ChangeLog[];
  total: number;
}

export interface FetchChangelogParams {
  entityId?: string;
  q?: string;
  field?: string;
  limit?: number;
  offset?: number;
  /** ISO date YYYY-MM-DD inclusive lower bound (Fix 5) */
  from?: string;
  /** ISO date YYYY-MM-DD inclusive upper bound (Fix 5) */
  to?: string;
  /** When true, ask the server to exclude system/automated rows before pagination. */
  hideSystem?: boolean;
}

export async function fetchChangelog(
  params: FetchChangelogParams = {},
): Promise<ChangelogListResponse> {
  return withFallback(
    "GET /changelog",
    async () => {
      const res = await api.get<ChangelogListResponse>("/changelog", { params });
      return res.data;
    },
    { items: FIXTURE_CHANGELOG, total: FIXTURE_CHANGELOG.length },
  );
}

export async function fetchAdminUsers(): Promise<AdminUserFixture[]> {
  return withFallback(
    "GET /admin/users",
    async () => {
      const res = await api.get<AdminUserFixture[]>("/admin/users");
      return res.data;
    },
    FIXTURE_ADMIN_USERS,
  );
}

export async function fetchProviders(): Promise<{ google: boolean }> {
  return withFallback(
    "GET /auth/providers",
    async () => {
      const res = await api.get<{ google: boolean }>("/auth/providers");
      return res.data;
    },
    { google: false },
  );
}

/**
 * GET /entities/{id}/renewal-status
 *
 * Does NOT fall back to fixtures — the status matrix is computed server-side
 * from cycles + dates; a fixture fallback would silently mislead.
 * Callers must handle the loading / error states explicitly.
 */
export async function fetchRenewalStatus(entityId: string): Promise<RenewalStatusResponse> {
  const res = await api.get<RenewalStatusResponse>(`/entities/${entityId}/renewal-status`);
  return res.data;
}

// Legacy field-based mark-renewed route was removed from the backend.
// Marking an obligation as renewed now goes through ObligationEditor
// → markObligationRenewed() below.

// ─── Obligation fetchers (typed-renewal-model) ────────────────────────────────

/**
 * GET /entities/{id}/obligations
 * Does NOT fall back to fixtures — obligation data is structured and server-computed.
 */
export async function fetchObligations(entityId: string): Promise<ObligationsResponse> {
  const res = await api.get<ObligationsResponse>(`/entities/${entityId}/obligations`);
  return res.data;
}

/**
 * POST /entities/{id}/obligations
 */
export async function createObligation(
  entityId: string,
  body: ObligationCreate,
): Promise<Obligation> {
  const res = await api.post<Obligation>(`/entities/${entityId}/obligations`, body);
  return res.data;
}

/**
 * PATCH /entities/{id}/obligations/{oid}
 */
export async function patchObligation(
  entityId: string,
  obligationId: string,
  body: ObligationPatch,
): Promise<Obligation> {
  const res = await api.patch<Obligation>(
    `/entities/${entityId}/obligations/${obligationId}`,
    body,
  );
  return res.data;
}

/**
 * DELETE /entities/{id}/obligations/{oid}?comment=...
 */
export async function deleteObligation(
  entityId: string,
  obligationId: string,
  comment: string,
): Promise<{ ok: boolean }> {
  const res = await api.delete<{ ok: boolean }>(
    `/entities/${entityId}/obligations/${obligationId}`,
    { params: { comment } },
  );
  return res.data;
}

/**
 * POST /entities/{id}/obligations/{oid}/mark-renewed
 */
export async function markObligationRenewed(
  entityId: string,
  obligationId: string,
  body: MarkObligationRenewedParams,
): Promise<Obligation> {
  const res = await api.post<Obligation>(
    `/entities/${entityId}/obligations/${obligationId}/mark-renewed`,
    body,
  );
  return res.data;
}

// ─── Ownership fetchers ───────────────────────────────────────────────────────

/**
 * GET /entities/{id}/ownership
 */
export async function fetchOwnership(entityId: string): Promise<OwnershipResponse> {
  const res = await api.get<OwnershipResponse>(`/entities/${entityId}/ownership`);
  return res.data;
}

/**
 * POST /entities/{id}/ownership  (add a stake)
 */
export async function createOwnershipStake(
  entityId: string,
  body: OwnershipStakeCreate,
): Promise<OwnershipResponse> {
  const res = await api.post<OwnershipResponse>(`/entities/${entityId}/ownership`, body);
  return res.data;
}

/**
 * PATCH /entities/{id}/ownership/{sid}
 */
export async function patchOwnershipStake(
  entityId: string,
  stakeId: string,
  body: OwnershipStakePatch,
): Promise<OwnershipResponse> {
  const res = await api.patch<OwnershipResponse>(
    `/entities/${entityId}/ownership/${stakeId}`,
    body,
  );
  return res.data;
}

/**
 * DELETE /entities/{id}/ownership/{sid}?comment=...
 */
export async function deleteOwnershipStake(
  entityId: string,
  stakeId: string,
  comment: string,
): Promise<OwnershipResponse> {
  const res = await api.delete<OwnershipResponse>(
    `/entities/${entityId}/ownership/${stakeId}`,
    { params: { comment } },
  );
  return res.data;
}

// ─── Director fetchers ────────────────────────────────────────────────────────

/**
 * GET /entities/{id}/directors
 * Does NOT fall back to fixtures — director data is structured and server-side.
 */
export async function fetchDirectors(entityId: string): Promise<DirectorsResponse> {
  const res = await api.get<DirectorsResponse>(`/entities/${entityId}/directors`);
  return res.data;
}

/**
 * POST /entities/{id}/directors  (add a director)
 */
export async function createDirector(
  entityId: string,
  body: DirectorCreate,
): Promise<Director> {
  const res = await api.post<Director>(`/entities/${entityId}/directors`, body);
  return res.data;
}

/**
 * PATCH /entities/{id}/directors/{did}
 */
export async function patchDirector(
  entityId: string,
  directorId: string,
  body: DirectorPatch,
): Promise<Director> {
  const res = await api.patch<Director>(
    `/entities/${entityId}/directors/${directorId}`,
    body,
  );
  return res.data;
}

/**
 * DELETE /entities/{id}/directors/{did}  (soft-delete, no comment required)
 */
export async function deleteDirector(
  entityId: string,
  directorId: string,
): Promise<{ ok: boolean }> {
  const res = await api.delete<{ ok: boolean }>(
    `/entities/${entityId}/directors/${directorId}`,
  );
  return res.data;
}

// ─── Typed entity fields ──────────────────────────────────────────────────────

/**
 * PATCH /entities/{id}/typed-fields
 */
export async function patchTypedFields(
  entityId: string,
  body: TypedEntityFields,
): Promise<Entity> {
  const res = await api.patch<Entity>(`/entities/${entityId}/typed-fields`, body);
  return res.data;
}

// ─── Country providers fetcher (full-data-visibility) ────────────────────────

export interface CountryProvidersPatch {
  lawyerTaxCorporate?: string | null;
  lawyerLabourHr?: string | null;
  litigationLawyer?: string | null;
  accountant?: string | null;
  statutoryAuditor?: string | null;
  banker?: string | null;
  debtCollectionAgency?: string | null;
  otherServiceProvider?: string | null;
  visaProvider?: string | null;
  healthcareProvider?: string | null;
  benefitsProvider?: string | null;
  payrollProvider?: string | null;
  taxGuideFirm?: string | null;
  taxGuideLastUpdated?: string | null;
  comment: string;
}

/**
 * PATCH /api/countries/{country_id}/providers
 * Requires admin or legal_advisor role.
 * Does NOT fall back to fixtures — mutation must fail loudly if backend is absent.
 */
export async function patchCountryProviders(
  countryId: string,
  body: CountryProvidersPatch,
): Promise<Country> {
  const res = await api.patch<Country>(`/countries/${countryId}/providers`, body);
  return res.data;
}

// ─── Autonomous-renewals fetchers (migration 0009) ────────────────────────────

/**
 * POST /entities/{id}/obligations/{oid}/confirm-estimate
 * Promotes an estimated obligation to confirmed.
 * Does NOT fall back to fixtures — mutation must fail loudly if backend is absent.
 */
export async function confirmEstimate(
  entityId: string,
  obligationId: string,
  comment: string,
): Promise<Obligation> {
  const res = await api.post<Obligation>(
    `/entities/${entityId}/obligations/${obligationId}/confirm-estimate`,
    { comment },
  );
  return res.data;
}

// ─── Regulatory Watch fetchers (migration 0010) ───────────────────────────────

export interface FetchCandidatesParams {
  country?: string;  // iso2
  domain?: RegwatchDomain;
  /** When set, filters to candidates whose affectedEntityIds contains this entity id. */
  entityId?: string;
  /**
   * Per-person review filter (default "active" server-side):
   *   active   — not reviewed by the current user
   *   reviewed — reviewed by the current user
   *   all      — both
   */
  reviewState?: "active" | "reviewed" | "all";
}

/**
 * GET /api/regulatory/candidates
 * Returns list of RuleChangeCandidate rows matching optional filters.
 * Falls back to empty array — page renders safely when backend isn't seeded yet.
 */
export async function fetchCandidates(
  params: FetchCandidatesParams = {},
): Promise<RuleChangeCandidate[]> {
  // Map reviewState → the backend's snake_case `review_state` query param.
  const { reviewState, ...rest } = params;
  const query: Record<string, string | undefined> = { ...rest };
  if (reviewState) query.review_state = reviewState;
  return withFallback(
    "GET /regulatory/candidates",
    async () => {
      const res = await api.get<RuleChangeCandidate[]>("/regulatory/candidates", {
        params: query,
      });
      return res.data;
    },
    [],
  );
}

/**
 * GET /api/regulatory/candidates/count
 * Returns the current user's UN-reviewed candidate count (per-person).
 * Used by the sidebar Regulatory Watch badge. Falls back to 0 so the badge
 * simply disappears when the backend isn't reachable.
 */
export async function fetchCandidateCount(): Promise<number> {
  return withFallback(
    "GET /regulatory/candidates/count",
    async () => {
      const res = await api.get<{ count: number }>("/regulatory/candidates/count");
      return res.data.count;
    },
    0,
  );
}

/**
 * POST /api/regulatory/candidates/{id}/review
 * Mark a candidate reviewed for the current user (per-person, idempotent → 204).
 * Does NOT fall back to fixtures — mutation must fail loudly.
 */
export async function markCandidateReviewed(id: string): Promise<void> {
  await api.post(`/regulatory/candidates/${id}/review`);
}

/**
 * DELETE /api/regulatory/candidates/{id}/review
 * Un-review a candidate for the current user (→ 204, back to active).
 * Does NOT fall back to fixtures — mutation must fail loudly.
 */
export async function unmarkCandidateReviewed(id: string): Promise<void> {
  await api.delete(`/regulatory/candidates/${id}/review`);
}

/**
 * GET /api/regulatory/sources?country=<iso2>&includeInactive=<bool>
 * Returns all non-deleted sources for the given country (or all countries when
 * countryIso2 is omitted). Falls back to empty array so the panel renders
 * safely when the backend has no sources seeded yet.
 */
export async function fetchRegulatorySourcesForCountry(
  countryIso2?: string,
  includeInactive = true,
): Promise<RegulatorySource[]> {
  return withFallback(
    "GET /regulatory/sources",
    async () => {
      const params: Record<string, string | boolean> = { includeInactive };
      if (countryIso2) params.country = countryIso2;
      const res = await api.get<RegulatorySource[]>("/regulatory/sources", { params });
      return res.data;
    },
    [],
  );
}

// ─── Regulatory Watch source mutations (NO withFallback — must fail loud) ─────

export interface SourceCreateBody {
  countryId: string;
  name: string;
  url: string;
  type: "registry" | "gazette" | "regulator" | "other";
  active?: boolean;
  /** Non-empty list of Entity IDs to scope this source to specific entities.
   *  Null / absent = country-scoped (legacy behaviour). */
  entityIds?: string[] | null;
}

export interface SourceUpdateBody {
  name?: string;
  url?: string;
  type?: "registry" | "gazette" | "regulator" | "other";
  active?: boolean;
  /** null = "do not change"; [] = revert to country-scoped; [...] = update scope. */
  entityIds?: string[] | null;
}

export interface SaveSourceFromCandidateBody {
  name: string;
  type: "registry" | "gazette" | "regulator" | "other";
}

/**
 * POST /api/regulatory/sources
 * Create a new curated regulatory source.
 * Does NOT fall back to fixtures — mutation must fail loudly.
 */
export async function createSource(body: SourceCreateBody): Promise<RegulatorySource> {
  const res = await api.post<RegulatorySource>("/regulatory/sources", body);
  return res.data;
}

/**
 * PATCH /api/regulatory/sources/{id}
 * Update name, url, type, or active toggle (pause/resume).
 * Does NOT fall back to fixtures.
 */
export async function updateSource(id: string, body: SourceUpdateBody): Promise<RegulatorySource> {
  const res = await api.patch<RegulatorySource>(`/regulatory/sources/${id}`, body);
  return res.data;
}

/**
 * DELETE /api/regulatory/sources/{id}
 * Soft-delete: sets deletedAt. Returns {ok: true}.
 * Does NOT fall back to fixtures.
 */
export async function deleteSource(id: string): Promise<{ ok: boolean }> {
  const res = await api.delete<{ ok: boolean }>(`/regulatory/sources/${id}`);
  return res.data;
}

/**
 * deleteRegulatorySource — alias for deleteSource.
 * Named export matching the VVV plan interface.
 */
export async function deleteRegulatorySource(id: string): Promise<void> {
  await api.delete(`/regulatory/sources/${id}`);
}

/**
 * POST /api/regulatory/candidates/{id}/save-source
 * One-click: add a candidate's sourceUrl to the country's curated sources.
 * Body: {name, type}. Returns 201 RegulatorySource.
 * Does NOT fall back to fixtures.
 */
export async function saveSourceFromCandidate(
  candidateId: string,
  body: SaveSourceFromCandidateBody,
): Promise<RegulatorySource> {
  const res = await api.post<RegulatorySource>(
    `/regulatory/candidates/${candidateId}/save-source`,
    body,
  );
  return res.data;
}

// ─── Country reassignment (entity-country-reassign) ──────────────────────────

/**
 * PATCH /entities/{entityId}/country
 * Role-gated: admin / legal_advisor.
 * The server sets `region` from the new country — the returned EntityRead
 * includes the updated region and the full new country object.
 * Does NOT fall back to fixtures — mutation must fail loudly.
 */
export async function reassignEntityCountry(
  entityId: string,
  countryId: string,
  comment?: string,
): Promise<Entity> {
  const body: { countryId: string; comment?: string } = { countryId };
  if (comment) body.comment = comment;
  const res = await api.patch<Entity>(`/entities/${entityId}/country`, body);
  return res.data;
}

// ─── Entity Documents (migration 0011) ───────────────────────────────────────

/**
 * GET /api/entities/{entityId}/documents
 * Returns the non-deleted documents for an entity, ordered createdAt DESC.
 * Does NOT fall back to fixtures — document data is server-stored binary content.
 * Callers must handle loading / error states explicitly.
 */
export async function fetchEntityDocuments(entityId: string): Promise<Document[]> {
  const res = await api.get<DocumentListResponse>(`/entities/${entityId}/documents`);
  return res.data.documents;
}

/**
 * POST /api/entities/{entityId}/documents
 * Upload a document via multipart/form-data.
 *
 * axios v1.x (project uses ^1.7.7): setting Content-Type: multipart/form-data in
 * the per-call config overrides the instance default (application/json from api.ts)
 * AND causes axios to auto-append the multipart boundary string.
 *
 * @param entityId   Target entity
 * @param formData   Pre-built FormData containing: name (string), notes? (string), file (File)
 * @param onUploadProgress  Optional callback — receives percent (0–100)
 */
export async function uploadEntityDocument(
  entityId: string,
  formData: FormData,
  onUploadProgress?: (percent: number) => void,
): Promise<Document> {
  const res = await api.post<Document>(
    `/entities/${entityId}/documents`,
    formData,
    {
      onUploadProgress: (e) => {
        if (onUploadProgress && e.total) {
          onUploadProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
      // Override instance default "application/json" — axios appends boundary automatically.
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return res.data;
}

/**
 * DELETE /api/entities/{entityId}/documents/{docId}
 * Soft-deletes the document (sets deletedAt). File is retained on disk.
 * Returns {ok: true} from the server.
 */
export async function deleteEntityDocument(
  entityId: string,
  docId: string,
): Promise<void> {
  await api.delete(`/entities/${entityId}/documents/${docId}`);
}

/**
 * PATCH /api/entities/{entityId}/documents/{docId}
 * Update a document's name and/or notes. Requires a non-empty comment for the
 * audit trail. Supports optimistic concurrency via expectedUpdatedAt.
 *
 * axios v1.x (project uses ^1.7.7): same Content-Type override as uploadEntityDocument.
 *
 * Error codes:
 *   409 { code: "stale_updated_at" } — concurrent modification detected.
 */
export async function patchEntityDocument(
  entityId: string,
  docId: string,
  patch: {
    name?: string;
    notes?: string | null;
    comment: string | null;
    expectedUpdatedAt?: string;
  },
): Promise<Document> {
  const fd = new FormData();
  if (patch.name !== undefined) fd.append("name", patch.name);
  if (patch.notes !== undefined) {
    // null → send "" so the backend normalises "" to null (clear the field);
    // any string → send as-is.
    fd.append("notes", patch.notes ?? "");
  }
  if (patch.comment) fd.append("comment", patch.comment);
  if (patch.expectedUpdatedAt) {
    fd.append("expectedUpdatedAt", patch.expectedUpdatedAt);
  }
  const res = await api.patch<Document>(
    `/entities/${entityId}/documents/${docId}`,
    fd,
    // Override instance default "application/json" — axios appends boundary automatically.
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return res.data;
}

/**
 * Build the authenticated download URL for a document.
 * Returns the absolute path that includes the Vite proxy prefix (/api/...).
 * The session cookie is carried automatically (same-origin, withCredentials).
 *
 * @param entityId    Entity that owns the document
 * @param docId       Document row id
 * @param disposition "inline" (open in browser tab — PDF/PNG/JPEG only on server)
 *                    "attachment" (force download, default)
 */
export function documentDownloadUrl(
  entityId: string,
  docId: string,
  disposition: "inline" | "attachment" = "attachment",
): string {
  return `/api/entities/${entityId}/documents/${docId}/download?disposition=${disposition}`;
}

// ───────────────────────────────────────────────────────────────────────────
// Admin invitations
// ───────────────────────────────────────────────────────────────────────────

import type {
  Invitation,
  InvitationCreatePayload,
  InvitationListResponse,
} from "./types";

export async function fetchInvitations(): Promise<InvitationListResponse> {
  const res = await api.get<InvitationListResponse>("/admin/invitations");
  return res.data;
}

export async function createInvitation(
  payload: InvitationCreatePayload,
): Promise<Invitation> {
  const res = await api.post<Invitation>("/admin/invitations", payload);
  return res.data;
}

export async function revokeInvitation(id: string): Promise<void> {
  await api.delete(`/admin/invitations/${id}`);
}

export async function revokeUserAccess(userId: string): Promise<void> {
  await api.post(`/admin/users/${userId}/revoke-access`);
}
